import { locationIdFormat } from "@redotech/redo-inventory-id";
import * as zExt from "@redotech/rpc/ext";
import * as z from "zod";
import { WarehouseGridSchema } from "./warehouse-grid";

export const DistanceMatrixSchema = z.record(
  z.string(),
  z.record(z.string(), z.union([z.number(), z.literal(Infinity)])),
);
export type DistanceMatrix = z.infer<typeof DistanceMatrixSchema>;

/**
 * Binary distance matrix backed by a Float64Array for near-instant deserialization.
 * Implements the same nested-object interface as DistanceMatrix via Proxy so
 * all existing callers (`matrix[from][to]`) work without changes.
 */
export class BinaryDistanceMatrix {
  private readonly distances: Float64Array;
  private readonly keyIndex: Map<string, number>;
  readonly keys: string[];
  readonly n: number;

  constructor(keys: string[], distances: Float64Array) {
    this.keys = keys;
    this.n = keys.length;
    this.distances = distances;
    this.keyIndex = new Map(keys.map((k, i) => [k, i]));
  }

  getDistance(from: string, to: string): number | undefined {
    const i = this.keyIndex.get(from);
    const j = this.keyIndex.get(to);
    if (i === undefined || j === undefined) {
      return undefined;
    }
    return this.distances[i * this.n + j]!;
  }

  /** Serialize to a binary buffer for Redis storage. */
  toBuffer(): Buffer {
    const keyStr = this.keys.join("\0") + "\0";
    const keyBuffer = Buffer.from(keyStr, "utf8");
    const header = Buffer.alloc(4);
    header.writeUInt32LE(this.n);
    return Buffer.concat([
      header,
      keyBuffer,
      Buffer.from(this.distances.buffer),
    ]);
  }

  /** Deserialize from a binary buffer. */
  static fromBuffer(buffer: Buffer): BinaryDistanceMatrix {
    const n = buffer.readUInt32LE(0);
    let offset = 4;
    const keys: string[] = [];
    for (let i = 0; i < n; i++) {
      const end = buffer.indexOf(0, offset);
      keys.push(buffer.toString("utf8", offset, end));
      offset = end + 1;
    }
    // Copy to an aligned buffer for Float64Array
    const floatBytes = buffer.subarray(offset);
    const aligned = new ArrayBuffer(floatBytes.length);
    new Uint8Array(aligned).set(floatBytes);
    const distances = new Float64Array(aligned);
    return new BinaryDistanceMatrix(keys, distances);
  }

  /** Convert from a plain DistanceMatrix object. */
  static fromPlain(matrix: DistanceMatrix): BinaryDistanceMatrix {
    const keys = Object.keys(matrix).sort();
    const n = keys.length;
    const distances = new Float64Array(n * n);
    distances.fill(Infinity);
    const keyIndex = new Map(keys.map((k, i) => [k, i]));
    for (const from of keys) {
      const i = keyIndex.get(from)!;
      const row = matrix[from]!;
      for (const to of Object.keys(row)) {
        const j = keyIndex.get(to);
        if (j !== undefined) {
          distances[i * n + j] = row[to] ?? Infinity;
        }
      }
    }
    return new BinaryDistanceMatrix(keys, distances);
  }

  /**
   * Returns a Proxy that implements the DistanceMatrix interface
   * (`matrix[from][to]`) backed by this binary matrix.
   */
  asProxy(): DistanceMatrix {
    const { keyIndex, keys, distances, n } = this;
    return new Proxy({} as DistanceMatrix, {
      get: (_target, from: string) => {
        const i = keyIndex.get(from);
        if (i === undefined) {
          return undefined;
        }
        return new Proxy({} as Record<string, number>, {
          get: (_t, to: string) => {
            const j = keyIndex.get(to);
            if (j === undefined) {
              return undefined;
            }
            return distances[i * n + j];
          },
          has: (_t, to: string) => keyIndex.has(to),
          ownKeys: () => keys,
          getOwnPropertyDescriptor: (_t, to: string) => {
            const j = keyIndex.get(to);
            if (j === undefined) {
              return undefined;
            }
            return {
              value: distances[i * n + j],
              writable: false,
              enumerable: true,
              configurable: true,
            };
          },
        });
      },
      has: (_target, from: string) => keyIndex.has(from),
      ownKeys: () => keys,
      getOwnPropertyDescriptor: (_target, from: string) => {
        if (!keyIndex.has(from)) {
          return undefined;
        }
        const i = keyIndex.get(from)!;
        return {
          value: new Proxy({} as Record<string, number>, {
            get: (_t, to: string) => {
              const j = keyIndex.get(to);
              if (j === undefined) {
                return undefined;
              }
              return distances[i * n + j];
            },
          }),
          writable: false,
          enumerable: true,
          configurable: true,
        };
      },
    });
  }
}

/**
 * Compacts a symmetric distance matrix to only store upper triangle (where from < to).
 * This roughly halves the storage size.
 */
export function compactDistanceMatrix(matrix: DistanceMatrix): DistanceMatrix {
  const compact: DistanceMatrix = {};
  for (const from of Object.keys(matrix)) {
    // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
    for (const to of Object.keys(matrix[from])) {
      if (from < to) {
        compact[from] ??= {};
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        compact[from][to] = matrix[from][to];
      }
    }
  }
  return compact;
}

/**
 * Expands a compacted triangular distance matrix back to full symmetric matrix.
 */
export function expandDistanceMatrix(compact: DistanceMatrix): DistanceMatrix {
  const full: DistanceMatrix = {};
  const allKeys = new Set<string>();

  for (const from of Object.keys(compact)) {
    allKeys.add(from);
    // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
    for (const to of Object.keys(compact[from])) {
      allKeys.add(to);
      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      const dist = compact[from][to];
      full[from] ??= {};
      full[to] ??= {};
      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      full[from][to] = dist;
      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      full[to][from] = dist;
    }
  }

  // Add self-distances (0)
  for (const key of allKeys) {
    full[key] ??= {};
    full[key][key] = 0;
  }

  return full;
}

/**
 * Checks if a distance matrix is in compact (triangular) format.
 * A compact matrix only has entries where from < to.
 */
export function isCompactDistanceMatrix(matrix: DistanceMatrix): boolean {
  for (const from of Object.keys(matrix)) {
    // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
    for (const to of Object.keys(matrix[from])) {
      if (from >= to) {
        return false;
      }
    }
  }
  return true;
}

export const WarehouseLayoutSchema = z.object({
  _id: zExt.objectId(),
  team: zExt.objectId(),
  crdbLocationId: locationIdFormat.schema.nullish(),
  warehouse: WarehouseGridSchema,
  createdAt: z.date(),
  updatedAt: z.date(),
  createdBy: zExt.objectId(),
  deletedAt: z.date().nullish(),
});

export type WarehouseLayout = z.infer<typeof WarehouseLayoutSchema>;
export interface IWarehouseLayoutSchema extends z.ZodType<WarehouseLayout> {}
